import ClientPortalAPIService from '../services/api/ClientPortal/ApiService'
import MapBox from './map'
import { cloneDeep, uniqBy } from 'lodash'
import moment from 'moment'
import { EventDetailsPanel } from '../components/EventDetailsPanel/EventDetailsPanel'
import User from './user'
import { SETTINGS } from '../SETTINGS'
import { debounce } from 'lodash'
import RiskIntelligenceAPIService from '../services/api/RiskIntelligence/ApiService'
import { maritimeMarketList } from '../services/map'
import Sentry from './SantryIO'

export class MaritimeAlertsModule {
    alerts = []
    latestAlerts = []
    showAlerts = false
    currentTimeFrame = SETTINGS.FILTERS_DEFAULT_TIMEFRAME
    servicesAndCategories = {}
    userPermissions = []
    startDate = moment.utc(moment().subtract(30, 'days')).format('YYYY-MM-DD')
    hasFirstTimeFetchedLatestAlerts = false

    constructor() { }

    initialize() {
        Promise.all([User.getLocalUserSettings(), User.getUserPermissions()]).then(values => {
            let userFilters = JSON.parse(values[0].filters)
            this.showAlerts = userFilters.enableAlerts
        })

        this.getAlerts(this.startDate).then(alerts => {
            this.latestAlerts = alerts
            this.hasFirstTimeFetchedLatestAlerts = true
            this.startLatestAlertsObserver()
        }) 
    }

    async getAlerts(startDate, endDate) {
        return await new Promise((resolve, reject) => {
            startDate = moment.utc(startDate).format('YYYY-MM-DD')
            if (endDate) {
                endDate = moment.utc(endDate).format('YYYY-MM-DD')
            }
            RiskIntelligenceAPIService.getIncidentAlerts(startDate, endDate).then(alerts => {
                this.alerts = alerts
                resolve(alerts)
                this.getCurrentAlertsAvailableCategories().then(servicesAndCategories => {
                    this.servicesAndCategories = servicesAndCategories

                    // Assure we apply the latest changes to user filters
                    User.getLocalUserSettings().then(settings => {
                        let userFilters = JSON.parse(settings.filters)

                        this.showAlerts = userFilters.enableAlerts
                        this.currentTimeFrame = userFilters.timeFrame

                        this.filter(this.servicesAndCategories, this.currentTimeFrame, endDate)

                        if (!this.eventsInitialized) {
                            this.addEventsListeners()
                            this.eventsInitialized = true
                        }
                    }).catch(error => {
                        Sentry.captureException(error)
                        reject(error)
                    })
                }).catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })
    }

    getAlertDetails(alertID) {
        return this.alerts.find(alert => alert.id === alertID)
    }

    getMaritimeAlerts() {
        return this.alerts
    }

    addEventsListeners() {
        
        MapBox.map.on('mouseenter', 'maritime-alerts', () => {
            MapBox.map.getCanvas().style.cursor = 'pointer'
        })

        MapBox.map.on('mouseleave', 'maritime-alerts', () => {
            MapBox.map.getCanvas().style.cursor = 'inherit'
        })
    }

    showAlert(alertDetails) {
        EventDetailsPanel.showMaritimeAlert(alertDetails)
    }

    startLatestAlertsObserver() {
        const ONE_MINUTE = 1000 * 60

        window.setInterval(() => {
            RiskIntelligenceAPIService.getIncidentAlerts(this.startDate).then(alerts => this.latestAlerts = alerts)
        }, ONE_MINUTE)
    }

    getAlertsFeatureCollection(incidents) {
        var featureCollection = {
            type: 'FeatureCollection',
            features: []
        }

        for (var alert of incidents) {
            var properties = {
                ['id']: alert.id,
                ['maritime']: `maritime-marker-${alert.type.id}`
            }
            var geometry = null
            geometry = {
                type: 'Point',
                coordinates: [alert.markerPosition.longitude, alert.markerPosition.latitude]
            }
            var feature = {
                type: 'Feature',
                id: alert.id,
                geometry: geometry,
                properties: properties
            }
            featureCollection.features.push(feature)
        }
        return featureCollection
    }

    async getModuleLatestAlerts() {
        const latestAlerts = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.hasFirstTimeFetchedLatestAlerts) {
                    resolve(this.latestAlerts)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return latestAlerts
    }

    display(filteredAlerts) {
        const alertsFeatureCollection = this.getAlertsFeatureCollection(filteredAlerts)
        const alertLayer = MapBox.map.getSource('maritime-alerts')

        if (alertLayer !== undefined) {
            alertLayer.setData(alertsFeatureCollection)
        } else {
            // See Avsec Alerts module for details
            window.setTimeout(() => {
                MapBox.map.addSource('maritime-alerts', {
                    'type': 'geojson',
                    'data': alertsFeatureCollection
                })
                MapBox.map.addLayer({
                    id: 'maritime-alerts',
                    type: 'symbol',
                    source: 'maritime-alerts',
                    layout: {
                        'visibility': this.showAlerts ? 'visible' : 'none',
                        'icon-image': 'maritime-alert',
                        'icon-size': 0.5,
                        'icon-allow-overlap': true,
                    }
                })
                const expression = ['match', ['get', 'maritime']]
                maritimeMarketList.forEach(c => expression.push(`maritime-marker-${c.id}`, `maritime-marker-${c.id}`))
                expression.push('maritime-alert')
                MapBox.map.setLayoutProperty('maritime-alerts', 'icon-image', expression)
            }, 2000)
        }

    }

    show() {
        this.showAlerts = true
        MapBox.map.setLayoutProperty('maritime-alerts', 'visibility', 'visible')
        this.debounceSavingUserSettings()
    }

    hide() {
        this.showAlerts = false
        MapBox.map.setLayoutProperty('maritime-alerts', 'visibility', 'none')
        this.debounceSavingUserSettings()
    }

    debounceSavingUserSettings = debounce(this.saveUserAlertsVisibilitySetting, 1500)

    saveUserAlertsVisibilitySetting() {
        User.getLocalUserSettings().then(settings => {
            let userFilters = JSON.parse(settings.filters)
            userFilters.enableAlerts = this.showAlerts

            User.updateUserFilters(userFilters)
        })
    }


    async getCurrentAlertsAvailableCategories() {
        let userSettings = await User.getLocalUserSettings()
        const parsedUserFilters = JSON.parse(userSettings.filters)
        const userAlertFilters = parsedUserFilters.maritimeFilters

        const uniqueServices = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.alerts.length > 0) {
                    let uniqueServices = {}

                    this.alerts.forEach(alert => {
                        if (uniqueServices.categories === undefined) {
                            uniqueServices = {
                                enabled: true,
                                categories: []
                            }

                            // If user had that unique service defined, then overide the value
                            if (userAlertFilters !== undefined) {
                                uniqueServices.enabled = userAlertFilters.enabled
                            }
                        }

                        // alert.Categories.forEach(category => {
                        const isExsistingCategory = uniqueServices.categories.find(existingCategory => existingCategory.name === alert.type.description)
                        if (isExsistingCategory === undefined) {
                            uniqueServices.categories.push({
                                name: alert.type.description,
                                enabled: true
                            })

                            // If user had that category defined, then overide the value
                            if (userAlertFilters.categories !== undefined) {
                                const existingUserCategoryIndex = userAlertFilters.categories.findIndex(existingCategory => existingCategory.name === alert.type.description)

                                if (existingUserCategoryIndex > -1) {
                                    const currentService = uniqueServices
                                    currentService.categories[currentService.categories.length - 1].enabled = userAlertFilters.categories[existingUserCategoryIndex].enabled
                                }
                            }
                        }
                        // })
                    })

                    resolve(uniqueServices)

                    window.clearInterval(observerInterval)
                }
            }, 100)
        })

        return uniqueServices
    }

    filter(servicesAndCategories, timeFrame, maximumPeriodDate) {
        if (timeFrame !== undefined) this.currentTimeFrame = timeFrame
        if (maximumPeriodDate === undefined) maximumPeriodDate = moment()
        this.servicesAndCategories = cloneDeep(servicesAndCategories)
        const beginningOfTheRange = moment(maximumPeriodDate).subtract(this.currentTimeFrame, 'days')
        const filteredAlerts = []
        if (servicesAndCategories.enabled) {
            servicesAndCategories.categories?.filter(res => {
                if (res.enabled) {
                    this.alerts.filter(res1 => {
                        if (moment(res1.date).isAfter(beginningOfTheRange)) {
                            if (res1.type.description == res.name) {
                                filteredAlerts.push(res1)
                            }
                        }
                    })
                }
            })
        }
        this.display(filteredAlerts)

    }
}

const MaritimeAlerts = new MaritimeAlertsModule()
export default MaritimeAlerts